import { useAuth } from "@clerk/clerk-react";
import React, { createContext, useContext, useState } from "react";

import { TimePeriod } from "features/Home/Sidebar/MetricsHelper/schema";
import { fetcherAuth } from "utils/api";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";

export type Metrics = {
  outreachCount: number;
  followUpCount: number;
  scheduledCount: number;
  replyCount: number;
  openedCount: number;
  openedPercentage: number;
  respondedCount: number;
  respondedPercentage: number;
};

export enum TimeFrame {
  daily = "daily",
  weekly = "weekly",
  monthly = "monthly",
}

export enum Tier {
  free = "free",
  pro = "pro",
  va = "va",
}

export type SubjectVariantStat = {
  subjectVariant: string;
  outreachCount: number;
  openedPercentage: number;
  respondedPercentage: number;
};

export type TemplateStat = {
  openedPercentage: number;
  outreachCount: number;
  respondedPercentage: number;
  templateId: number;
  subjects?: SubjectVariantStat[];
};

export type TierStat = {
  outreachCount: number;
};

interface MetricsContextInterface {
  fetchMetrics: (timePeriod: TimePeriod, showLoading?: boolean) => void;
  metricsLoading: { [key in TimePeriod]: boolean };
  metrics: {
    [key in TimePeriod]: Metrics | undefined;
  };
  fetchMetricsByTemplate: () => void;
  templateStats: TemplateStat[];
  bestPerformingTemplate: number;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  fetchMetrics: defaultContextMissingFunction,
  metricsLoading: {
    today: false,
    yesterday: false,
    this_week: false,
    last_week: false,
    this_month: false,
    last_month: false,
  },
  metrics: {
    today: undefined,
    yesterday: undefined,
    this_week: undefined,
    last_week: undefined,
    this_month: undefined,
    last_month: undefined,
  },
  fetchMetricsByTemplate: defaultContextMissingFunction,
  templateStats: [],
  bestPerformingTemplate: -1,
};

const MetricsContext = createContext<MetricsContextInterface>(defaultInterface);

interface MetricsProviderProps {
  children: React.ReactNode;
}

const MetricsProvider = ({ children }: MetricsProviderProps) => {
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const [metricsLoading, setMetricsLoading] = useState<{
    [key in TimePeriod]: boolean;
  }>({
    today: false,
    yesterday: false,
    this_week: false,
    last_week: false,
    this_month: false,
    last_month: false,
  });
  const [metrics, setMetrics] = useState<{
    [key in TimePeriod]: Metrics | undefined;
  }>({
    today: undefined,
    yesterday: undefined,
    this_week: undefined,
    last_week: undefined,
    this_month: undefined,
    last_month: undefined,
  });
  const [templateStats, setTemplateStats] = useState<TemplateStat[]>([]);
  const [bestPerformingTemplate, setBestPerformingTemplate] =
    useState<number>(-1);

  const fetchMetrics = async (
    timePeriod: TimePeriod,
    showLoading: boolean = true,
  ) => {
    if (metricsLoading[timePeriod]) {
      return;
    }

    try {
      if (showLoading) {
        setMetricsLoading((prev) => ({ ...prev, [timePeriod]: true }));
      }
      const metrics = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-metrics?time_period=${timePeriod}`,
        "GET",
        {},
      );
      setMetrics((prev) => ({ ...prev, [timePeriod]: metrics }));
    } catch (error) {
      setAlert(
        "Something went wrong and your metrics could not be fetched. Please refresh the page and try again",
        "error",
      );
    } finally {
      setMetricsLoading((prev) => ({ ...prev, [timePeriod]: false }));
    }
  };

  const fetchMetricsByTemplate = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-metrics/by-template`,
      );

      if (res && res?.length > 0) {
        const bestPerformingTemplate = res.reduce(
          (max: TemplateStat, stat: TemplateStat) =>
            max.respondedPercentage > stat.respondedPercentage ? max : stat,
        );
        if (bestPerformingTemplate?.respondedPercentage > 0) {
          setBestPerformingTemplate(bestPerformingTemplate.templateId);
        }
      }

      setTemplateStats(res);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  return (
    <MetricsContext.Provider
      value={{
        fetchMetrics,
        metricsLoading,
        metrics,
        fetchMetricsByTemplate,
        bestPerformingTemplate,
        templateStats,
      }}
    >
      {children}
    </MetricsContext.Provider>
  );
};

export { MetricsProvider, MetricsContext };
